"use client"
import { useState, useEffect } from "react"
import { X, Mail, Send, CheckCircle2, AlertCircle } from "lucide-react"

// Free, no-backend email delivery via FormSubmit.co.
// Submissions are forwarded to the activated inbox WITHOUT exposing the email
// address in this client code. Paste your FormSubmit alias (the random string
// from your activation email or the formsubmit.co dashboard) below — e.g.
// "a1b2c3d4e5f6...". Do NOT put the raw email here, or it becomes public.
const FORMSUBMIT_ALIAS = "c892ae2df3489fc8498f3b5d75d58474"
const FORMSUBMIT_ENDPOINT = `https://formsubmit.co/ajax/${FORMSUBMIT_ALIAS}`

export function EnquiryModal({ open, onClose }) {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [message, setMessage] = useState("")
  const [status, setStatus] = useState("idle") // idle | sending | success | error

  // Close on Escape key
  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onClose])

  // Reset the form whenever the modal is freshly opened
  useEffect(() => {
    if (open) {
      setStatus("idle")
      setName("")
      setEmail("")
      setMessage("")
    }
  }, [open])

  if (!open) return null

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus("sending")
    try {
      const res = await fetch(FORMSUBMIT_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name,
          email,
          message,
          _subject: "New enquiry from CTS website",
          _template: "table",
        }),
      })
      const data = await res.json()
      if (res.ok && (data.success === "true" || data.success === true)) {
        setStatus("success")
      } else {
        setStatus("error")
      }
    } catch (err) {
      setStatus("error")
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-green-50/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Mail className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-green-800">Get in Touch</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 hover:bg-gray-100 p-2 rounded-full transition-colors"
            aria-label="Close enquiry form"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {status === "success" ? (
            <div className="text-center py-6">
              <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-4" />
              <h4 className="text-xl font-semibold text-green-800 mb-2">Thank you!</h4>
              <p className="text-green-600">
                Your enquiry has been sent. We'll get back to you soon.
              </p>
              <button
                onClick={onClose}
                className="mt-6 bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 px-8 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-gray-500 mb-2">
                Have a question? Send us an enquiry and we'll get back to you.
              </p>

              <div>
                <label htmlFor="enquiry-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  id="enquiry-name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-800 focus:border-green-500 focus:ring-2 focus:ring-green-200 focus:outline-none transition"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="enquiry-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="enquiry-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-800 focus:border-green-500 focus:ring-2 focus:ring-green-200 focus:outline-none transition"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label htmlFor="enquiry-message" className="block text-sm font-medium text-gray-700 mb-1">
                  Enquiry
                </label>
                <textarea
                  id="enquiry-message"
                  required
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-800 focus:border-green-500 focus:ring-2 focus:ring-green-200 focus:outline-none transition resize-none"
                  placeholder="How can we help?"
                />
              </div>

              {status === "error" && (
                <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Something went wrong. Please try again in a moment.</span>
                </div>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 px-8 rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                {status === "sending" ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Enquiry
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
